// ========== React & Libraries ==========
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';

// ========== Commons ==========

// ========== Utils & Helpers ==========
import { getCoachPayment, getTotalSumFromLifetime, getUser } from '@/lib/api-calls.js';
import { validateRouteRole } from '@/lib/routing.js';

// ========== Table Components ==========
import { EntityTable } from '@/components/table/entity-table.js';
import { TableErrorState } from '@/components/table/table-error-state.js';

// ========== Layout Components ==========
import { InfoPage } from '@/components/info-page.js';

// ========== Users/Teachers Components ==========
import { FrequentTimeZones, isAirAdmin } from '@airreading/common';

import { formatCurrency } from '@/lib/common-utils.js';

import { coachesTabs } from '../../-components/tabs.js';
import { QueryKeys } from '@/lib/queries.js';
import { CreatePayment } from '../../-components/payments/create-payment.js';
import { airColumnHelper } from '@/lib/tables.js';
import {
  CoachPaymentReportForRecipient,
  PayCycleDTO,
  coachPaymentKind,
  coachPaymentReportForRecipientSchema,
} from '@airreading/common/data';
import { KebabOptions } from '../../-components/payments/kebab.js';
import { CoachPaymentKindTag } from '../../-components/payments/coach-payment-kind-tag.js';
import { FromOptions } from '../../-components/payments/from-options.js';
import { Badge } from '@/components/ui/badge.js';
import { Temporal } from 'temporal-polyfill';

const columnHelper = airColumnHelper<CoachPaymentReportForRecipient>(coachPaymentReportForRecipientSchema.keyof());

const coachPaymentReportTableColumns = (currentPaydate: Temporal.PlainDate) => [
  columnHelper.accessor('payPeriodInterval', {
    id: 'payPeriod',
    header: 'Period',
    cell: ({ getValue }) => getValue(),
    enableSorting: false,
  }),

  columnHelper.accessor('payDate', {
    id: 'paymentDate',
    header: 'Pay Initiation Date',
    cell: ({ getValue, row }) => {
      const isCurrent =
        currentPaydate &&
        PayCycleDTO.isWithinWindow(
          Temporal.PlainDate.from(currentPaydate),
          PayCycleDTO.fromPayPeriodId(row.original.payCycleId),
        );

      return (
        <div className="flex items-center gap-2">
          <span>{getValue()}</span>
          {isCurrent && (
            <Badge
              variant="outline"
              className="text-orange-600 bg-[#FFF6D4] border-[#FFF6D4] font-semibold"
              data-testid={`teacher-payments-table-badge-current-payment-${row.index}`}
            >
              Current Payment
            </Badge>
          )}
        </div>
      );
    },
    enableSorting: false,
  }),

  columnHelper.accessor('depositPostedDate', {
    id: 'depositPostedDate',
    header: 'Delivery (1-3 business days)',
    cell: ({ getValue }) => getValue(),
    enableSorting: false,
  }),

  columnHelper.accessor('type', {
    id: 'paymentType',
    header: 'Type',
    cell: ({ getValue, row }) => {
      return <CoachPaymentKindTag kind={getValue()} data-testid={`teacher-payments-table-badge-type-${row.index}`} />;
    },
    enableSorting: false,
  }),

  columnHelper.accessor((row) => row, {
    id: 'amount',
    header: 'Gross / Net Earnings',
    cell: ({ getValue, row }) => {
      const paymentRow = getValue();

      const { totalAmountInCents } = paymentRow;

      const amount = formatCurrency(totalAmountInCents / 100);

      const isTeachingPayment = paymentRow.type === coachPaymentKind.enum.teaching;

      if (isTeachingPayment) {
        return (
          <Link
            to={'/teachers/$teacherId/payments/teaching/$payCycleId'}
            params={{ teacherId: paymentRow.coach.id, payCycleId: paymentRow.payCycleId }}
            search={{
              pageIndex: 0,
              pageSize: 20,
            }}
            className="text-blue-600 underline underline-offset-2"
            data-testid={`teacher-payments-table-link-earnings-${row.index}`}
          >
            {amount}
          </Link>
        );
      }

      return <span data-testid={`teacher-payments-table-cell-amount-text-${row.index}`}>{amount}</span>;
    },
    enableSorting: false,
  }),

  columnHelper.accessor((row) => row, {
    id: 'actions',
    header: 'Actions',
    cell: ({ getValue, row }) => {
      const paymentRow = getValue();

      return (
        <KebabOptions
          id={paymentRow.id}
          teacherId={paymentRow.coach.id}
          type={paymentRow.type}
          from={FromOptions.payments}
          notes={paymentRow.notes}
          data-testid={`teacher-payments-table-button-actions-${row.index}`}
        />
      );
    },
  }),
];

export const Route = createFileRoute('/_layout/teachers/$teacherId/payments/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: TeacherPay,
  validateSearch: (search) => {
    return {
      editId: search.editId as string | undefined,
    };
  },
});

function TeacherPay() {
  const { teacherId } = Route.useParams();
  const { data: user } = useSuspenseQuery({
    queryKey: QueryKeys.getUser(teacherId),
    queryFn: () => getUser(teacherId),
  });

  const { data, isPending, refetch, error } = useQuery(
    getCoachPayment({
      pageIndex: 0,
      pageSize: 20,
      filters: {
        coachId: teacherId,
      },
    }),
  );

  const { data: totalSum } = useQuery(
    getTotalSumFromLifetime({
      filters: { coachId: teacherId },
    }),
  );

  const currentPaydate = PayCycleDTO.paycycleWindow(
    Temporal.Now.plainDateISO(user.timeZone ?? FrequentTimeZones.UsCentral),
  );

  const table = useReactTable({
    data: data?.payments ?? [],
    columns: coachPaymentReportTableColumns(currentPaydate.paymentPlainDate),
    getRowId: (row) => row.id,
    rowCount: data?.total ?? 0,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    state: {
      pagination: {
        pageIndex: 0,
        pageSize: 20,
      },
    },
  });

  const search = Route.useSearch();

  return (
    <InfoPage
      headerText={user.fullNameOrEmail}
      tabs={coachesTabs({ teacherId })}
      breadcrumbs={{
        linkItems: [
          {
            label: 'Teachers',
            link: {
              to: '/teachers',
            },
          },
        ],
        currentPage: user.fullNameOrEmail ?? '',
      }}
    >
      <EntityTable
        table={table}
        showPagination
        data-testid="teacher-payments-table"
        rowTestId={(_, rowIndex) => `teacher-payments-table-row-${rowIndex}`}
        cellTestId={(_, columnId, rowIndex) => `teacher-payments-table-cell-${columnId}-${rowIndex}`}
        endMessageTestId="teacher-payments-table-end-message"
        topBarRightComponents={
          <CreatePayment
            from={FromOptions.payments}
            editId={search?.editId}
            coach={{
              id: teacherId,
              fullNameOrEmail: user.fullNameOrEmail ?? '',
            }}
            data-testid="teacher-payments-button-add"
          />
        }
        topBarBottomComponents={
          <p className="text-sm font-semibold" data-testid="teacher-payments-sum">
            SUM{' '}
            <span className="font-bold text-2xl" data-testid="teacher-payments-sum-amount">
              {formatCurrency((totalSum ?? 0) / 100)}
            </span>
          </p>
        }
        isLoading={isPending}
        hasError={!!error}
        emptyState="No payments found"
        errorState={<TableErrorState refetchAction={refetch} />}
      />
    </InfoPage>
  );
}
